import React from 'react';
import { useFormikContext } from 'formik';
import { alpha, Grid, styled, Box, Typography, Button } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useSnackbar } from 'notistack';

import { TextFormField } from 'lib/form/fields/TextFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { EGAEventName, useGAHelpers } from 'lib/hooks/useGAHelper';
import { TrackingLinkType } from '../trackingLinks';

const StyledButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.purple.core,
    color: theme.palette.common.white,
    borderColor: theme.palette.purple.core,
    textTransform: 'none',
    paddingLeft: theme.spacing(1, 2),
    '&:hover': {
        backgroundColor: alpha(theme.palette.purple.core, 0.9),
        borderColor: theme.palette.purple.core,
        color: theme.palette.common.white
    }
}));

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'start',
    gap: theme.spacing(2),
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    height: '100%',
    backgroundColor: alpha(theme.palette.purple.core, 0.15),
    backdropFilter: 'blur(1px)',
    padding: theme.spacing(2)
}));

const LockedGrid = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${alpha(theme.palette.purple.core, 0.25)}`,
    position: 'relative',
    overflow: 'hidden'
}));

const UnlockedGrid = styled(Grid)({
    opacity: 1,
    pointerEvents: 'auto'
});

interface UTMFormProps {
    utmParametersEnabled: boolean;
    selectedType: TrackingLinkType;
}

interface UTMFormValues {
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmContent?: string;
    utmTerm?: string;
}

export const UTMForm = ({ utmParametersEnabled, selectedType }: UTMFormProps) => {
    const { logUserEvent } = useGAHelpers();
    const { enqueueSnackbar } = useSnackbar();
    const { values } = useFormikContext<UTMFormValues>();
    const Wrapper = utmParametersEnabled ? UnlockedGrid : LockedGrid;
    const coreRequired =
        utmParametersEnabled &&
        Boolean(
            values?.utmSource ||
                values?.utmMedium ||
                values?.utmCampaign ||
                values?.utmContent ||
                values?.utmTerm
        );

    const handleUnlockUTMTracking = React.useCallback(() => {
        logUserEvent(EGAEventName.CampaignLinksPageIAmInterestedClick);
        enqueueSnackbar("Thanks — we'll be in touch with more information.", { variant: 'success' });
    }, [logUserEvent, enqueueSnackbar]);
    return (
        <Wrapper container spacing={3}>
            {!utmParametersEnabled && (
                <>
                    <Grid size={12}>
                        <StyledBox>
                            <Box mt={1}>
                                <AutoAwesomeIcon sx={{ fontSize: '1.5rem', color: 'purple.core' }} />
                            </Box>
                            <Box height="100%" display="flex" flexDirection="column" gap={0.5}>
                                <Typography variant="body1" color="purple.core">
                                    <b> Premium feature</b>
                                </Typography>
                                <Typography variant="caption" color="purple.core">
                                    Free links still track total clicks. Upgrade to Premium to tag links with
                                    UTM parameters and assign unique table numbers for more targeted in-store
                                    ordering journeys.
                                </Typography>
                                <Box>
                                    <StyledButton
                                        size="small"
                                        variant="contained"
                                        onClick={handleUnlockUTMTracking}
                                    >
                                        Unlock Advanced Tracking
                                    </StyledButton>
                                </Box>
                            </Box>
                        </StyledBox>
                    </Grid>
                    <Grid size={12} height="80px"></Grid>
                </>
            )}
            {selectedType === TrackingLinkType.EXTERNAL_LINK && (
                <Grid size={12}>
                    <TextFormField
                        name="tableNumber"
                        label={<MuiFormLabel>Table Number (Optional)</MuiFormLabel>}
                        variant="outlined"
                        placeholder="e.g. 12"
                    />
                </Grid>
            )}
            <Grid size={12}>
                <TextFormField
                    name="utmSource"
                    label={<MuiFormLabel required={coreRequired}>UTM Source</MuiFormLabel>}
                    variant="outlined"
                    placeholder="e.g. newsletter"
                />
            </Grid>
            <Grid size={12}>
                <TextFormField
                    name="utmMedium"
                    label={<MuiFormLabel required={coreRequired}>UTM Medium</MuiFormLabel>}
                    variant="outlined"
                    placeholder="e.g. email"
                />
            </Grid>
            <Grid size={12}>
                <TextFormField
                    name="utmCampaign"
                    label={<MuiFormLabel required={coreRequired}>UTM Campaign</MuiFormLabel>}
                    variant="outlined"
                    placeholder="e.g. october_launch"
                />
                {coreRequired && (
                    <Typography mt={2} ml={2} variant="caption" color="text.secondary">
                        UTM source, medium, and campaign must be filled in together, or left blank.
                    </Typography>
                )}
            </Grid>
            <Grid size={6}>
                <TextFormField
                    name="utmContent"
                    label="UTM Content (Optional)"
                    variant="outlined"
                    placeholder="e.g. hero_banner"
                />
            </Grid>
            <Grid size={6}>
                <TextFormField
                    name="utmTerm"
                    label="UTM Term (Optional)"
                    variant="outlined"
                    placeholder="e.g. discount"
                />
            </Grid>
        </Wrapper>
    );
};
